/**
 * ExecutiveReportPage — V2 dashboard-style executive report.
 *
 * Fixed timeframe (Yesterday). Displays 5 sections: excess alarms table,
 * reason chart, care setting averages, response distribution charts,
 * and top 10 locations. Uses AlertDetails ReportFilterDrawer for filters.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession, getCampusId, getUserId, getSessionId, getCampusList,
    updateSessionCampus, getUsername, getCampusConfig,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, getToday } from '../../../libs.jsx';
import {
    fetchExecutiveReport, fetchExecutiveFilterOptions, saveExecThreshold,
} from './api/executiveApi.js';
import { addEventNote, editEventReason, fetchPreformNotes } from '../alert-details/api/alertDetailsApi.js';
import { fetchCallLogsReport } from '../call-logs/api/callLogsReportApi.js';

import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import EventNoteDialog from '../alert-details/components/EventNoteDialog.jsx';
import EditReasonDialog from '../alert-details/components/EditReasonDialog.jsx';
import ExcessAlarmsTable from './components/ExcessAlarmsTable.jsx';
import ReasonChart from './components/ReasonChart.jsx';
import CareSettingTable from './components/CareSettingTable.jsx';
import ResponseDistribution from './components/ResponseDistribution.jsx';
import TopLocationsTable from './components/TopLocationsTable.jsx';

import './executive-report-page.css';

function emptyReportFilters() {
    return {
        areas: [], areaGroups: [], areaTypes: [],
        residents: [], residentGroups: [],
        users: [], userGroups: [],
        deviceCategories: [], devices: [],
        eventTypes: [], reasons: [],
        responseTime: { enabled: false, operator: 'lt', value: 60 },
    };
}

const ExecutiveReportPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = campusConfig?.tz_offset;

    // -- Report state --
    const [status, setStatus] = useState('init');
    const [excessiveAlerts, setExcessiveAlerts] = useState([]);
    const [reasonData, setReasonData] = useState({});
    const [areaTypesResponse, setAreaTypesResponse] = useState([]);
    const [areaTypesChart, setAreaTypesChart] = useState({});
    const [dailyAreas, setDailyAreas] = useState([]);

    // -- Session feature flags --
    const eventNotesActive = Boolean(campusConfig?.event_notes_active);
    const eventNotesPreform = Boolean(campusConfig?.event_notes_preform);
    const eventNotesFreeform = Boolean(campusConfig?.event_notes_freeform);
    const isReasonEditable = Boolean(campusConfig?.is_reason_editable);
    // Grandstream campuses get a Calls column (call logs merged into events by event_id).
    const isGrandstream = Boolean(campusConfig?.is_grandstream);

    // -- Note / Reason dialog state --
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteTarget, setNoteTarget] = useState(null);
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [reasonTarget, setReasonTarget] = useState(null);
    const [prenoteData, setPrenoteData] = useState([]);
    const [saving, setSaving] = useState(false);

    // -- Exec threshold --
    const defaultExecTime = campusConfig?.exec_excess_time || 20;
    const [execTime, setExecTime] = useState(defaultExecTime);
    const [savingThreshold, setSavingThreshold] = useState(false);

    // -- Navigation state (from ScheduledReportsPage "Run Report Local") --
    const location = useLocation();
    const navStateRef = useRef(location.state);
    const navState = navStateRef.current;

    // -- Filters — pre-fill from navigation state if present --
    const [reportFilters, setReportFilters] = useState(() => {
        if (navState?.filters) return { ...emptyReportFilters(), ...navState.filters };
        return emptyReportFilters();
    });
    const [filterOptions, setFilterOptions] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Time range (fixed Yesterday) --
    const [rangeId] = useState(2);
    const [rangeName] = useState('Yesterday');
    const [start] = useState(() => { const d = getToday(-1); d.setHours(0,0,0,0); return d; });
    const [end] = useState(() => { const d = getToday(-1); d.setHours(23,59,59,999); return d; });

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const shouldRerunRef = useRef(false);
    const autoRunRef = useRef(true);

    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // -- Load filter options + preform notes on mount / campus change --
    useEffect(() => {
        if (!campusId) return;
        fetchExecutiveFilterOptions(campusId).then(setFilterOptions);
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
        if (eventNotesActive) {
            fetchPreformNotes(campusId).then(setPrenoteData);
        }
    }, [campusId, eventNotesActive]);

    // -- Auto-run on first load --
    useEffect(() => {
        if (autoRunRef.current) {
            autoRunRef.current = false;
            handleRunReport();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // -- Run Report --
    const handleRunReport = useCallback(async () => {
        setStatus('loading');

        const now = new Date();
        const endToUse = end > now ? now : end;
        const stD = formatDateIso(start);
        const enD = formatDateIso(endToUse);

        // Drawer keys reasons by cloud_reason_code_id; combine with each row's
        // matching reason_code_id.
        const cloudReasonIds = reportFilters.reasons || [];
        const reasonIds = cloudReasonIds
            .map((cid) => {
                const rc = (filterOptions.reasonCodes || []).find((r) => String(r.cloud_reason_code_id) === String(cid));
                return rc?.reason_code_id;
            })
            .filter((id) => id != null);
        const combinedReasonIds = [...new Set([...cloudReasonIds, ...reasonIds])];
        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: combinedReasonIds.join(','),
        };

        const result = await fetchExecutiveReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            filters: filterPayload,
            execTime,
        });

        if (result.ErrorCode) {
            const errMap = {
                11: 'Report timed out. Contact your customer success manager for large reports.',
                10: 'An unknown error occurred. Please contact customer success if the problem persists.',
                12: 'Request unauthorized. Service may be unavailable.',
            };
            showToast('error', errMap[result.ErrorCode] || 'Report request failed.');
            setStatus('init');
            return;
        }

        let excessAlerts = result.excessiveAlerts || [];

        // Grandstream campuses: fetch call logs for the same window and merge them
        // into the excess-alarm events by event_id (mirrors how notes ride on each
        // row) so the table can show a per-event call count + hover. Non-fatal on
        // failure — the report still renders, just without call badges. Call logs
        // proc wants DATE strings (formatDateIso already yields yyyy-mm-dd).
        if (isGrandstream && excessAlerts.length) {
            try {
                const callResp = await fetchCallLogsReport({
                    campusId,
                    startDate: stD,
                    endDate: enD,
                });
                const callRecords = callResp?.records || [];
                if (callRecords.length) {
                    const callsByEvent = new Map();
                    callRecords.forEach((c) => {
                        const key = String(c.event_id ?? '');
                        if (!key) return;
                        if (!callsByEvent.has(key)) callsByEvent.set(key, []);
                        callsByEvent.get(key).push(c);
                    });
                    callsByEvent.forEach((arr) => arr.sort(
                        (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
                    ));
                    excessAlerts = excessAlerts.map((r) => ({
                        ...r,
                        calls: callsByEvent.get(String(r.event_id ?? '')) || [],
                    }));
                }
            } catch (err) {
                console.error('[ExecutiveReportPage] Call logs merge failed:', err);
            }
        }

        setExcessiveAlerts(excessAlerts);
        setReasonData(result.reasonData || {});
        setAreaTypesResponse(result.areaTypesResponse || []);
        setAreaTypesChart(result.areaTypesResponseChart?.chart || {});
        setDailyAreas(result.dailyAreas || []);
        setStatus('ready');
    }, [campusId, start, end, reportFilters, execTime, showToast, isGrandstream]);

    // -- Re-run after filter apply --
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    // -- Campus change --
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setStatus('init');
        setExcessiveAlerts([]);
        setReasonData({});
        setAreaTypesResponse([]);
        setAreaTypesChart({});
        setDailyAreas([]);
        const s = getSession();
        const newCfg = s?.campusData?.[val.toString()];
        setExecTime(newCfg?.exec_excess_time || 20);
    }, []);

    // -- Save threshold --
    const handleSaveThreshold = useCallback(async () => {
        setSavingThreshold(true);
        const result = await saveExecThreshold(campusId, execTime, defaultExecTime, getUserId(), getSessionId());
        setSavingThreshold(false);
        if (result[0]) {
            showToast('success', `Threshold saved to ${execTime} minutes`);
            // Update session
            const s = getSession();
            if (s?.campusData?.[campusId.toString()]) {
                s.campusData[campusId.toString()].exec_excess_time = execTime;
            }
        } else {
            showToast('error', result[1] || 'Failed to save threshold');
        }
    }, [campusId, execTime, defaultExecTime, showToast]);

    // -- Filter handlers --
    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    // Filter chips
    const reportFilterChips = useMemo(() => {
        const chips = [];
        const labelMap = {
            areas: 'Areas', areaGroups: 'Area Groups', areaTypes: 'Area Types',
            residents: 'Residents', residentGroups: 'Resident Groups',
            users: 'Users', userGroups: 'User Groups',
            deviceCategories: 'Device Categories', devices: 'Devices',
            eventTypes: 'Event Types', reasons: 'Reasons',
        };
        Object.entries(reportFilters).forEach(([key, vals]) => {
            if (key === 'responseTime') {
                if (vals?.enabled) chips.push({ key, label: `Response Time: ${vals.operator} ${vals.value}min` });
            } else if (Array.isArray(vals) && vals.length > 0) {
                chips.push({ key, label: `${labelMap[key] || key}: ${vals.length}` });
            }
        });
        return chips;
    }, [reportFilters]);

    const activeFilterCount = reportFilterChips.length;

    // -- Note dialog handlers --
    const handleOpenNoteDialog = useCallback((event) => {
        setNoteTarget(event);
        setNoteDialogOpen(true);
    }, []);

    const handleSaveNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Note added successfully.');
            setNoteDialogOpen(false);
            setNoteTarget(null);
            handleRunReport();
        } else {
            showToast('error', result[1] || 'Failed to add note.');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Reason dialog handlers --
    const handleOpenReasonDialog = useCallback((event) => {
        setReasonTarget(event);
        setReasonDialogOpen(true);
    }, []);

    const handleSaveReason = useCallback(async (eventId, newReasonId, oldReasonId) => {
        setSaving(true);
        const result = await editEventReason(eventId, newReasonId, oldReasonId, campusId, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Reason updated successfully.');
            setReasonDialogOpen(false);
            setReasonTarget(null);
            handleRunReport();
        } else {
            showToast('error', result[1] || 'Failed to update reason.');
        }
    }, [campusId, showToast, handleRunReport]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('success', 'Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast('error', result[1] || 'Failed to create schedule.'); }
    }, [campusId, showToast]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`ap-toast ${toast.type === 'success' ? 'ap-toast--success' : 'ap-toast--error'}`} role="status" aria-live="polite">
                            {toast.type === 'success' ? (
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                </svg>
                            )}
                            <span>{toast.message}</span>
                            <button type="button" onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }} className="ap-toast__close" aria-label="Dismiss">
                                <svg className="ap-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Executive</span>
                                </div>
                                <div className="ar-campus-row">
                                    <label htmlFor="ex-campus-selector" className="ar-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="ex-campus-selector" value={campusId} onChange={handleCampusChange} className="ar-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Executive Report</h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setScheduleDrawerOpen(true)}>
                                        <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        {/* Filter chips */}
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (
                                    <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                                ))}
                                <button className="ar-filter-chips__clear" onClick={handleClearReportFilters}>Clear All</button>
                            </div>
                        ) : null}

                        {/* Init */}
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Click <strong>Run Report</strong> to generate yesterday's executive summary.</p>
                            </div>
                        ) : null}

                        {/* Loading */}
                        {status === 'loading' ? (
                            <div className="ar-loading"><div className="ar-loading__spinner" /><p>Loading executive report...</p></div>
                        ) : null}

                        {/* Report sections */}
                        {status === 'ready' ? (
                            <div className="ex-sections">
                                <ExcessAlarmsTable
                                    data={excessiveAlerts}
                                    execTime={execTime}
                                    onExecTimeChange={setExecTime}
                                    onSaveThreshold={handleSaveThreshold}
                                    savingThreshold={savingThreshold}
                                    tzOffset={tzOffset}
                                    onAddNote={handleOpenNoteDialog}
                                    onEditReason={handleOpenReasonDialog}
                                    eventNotesActive={eventNotesActive}
                                    isReasonEditable={isReasonEditable}
                                    isGrandstream={isGrandstream}
                                />
                                <ReasonChart reasonData={reasonData} />
                                <CareSettingTable data={areaTypesResponse} />
                                <ResponseDistribution chartData={areaTypesChart} />
                                <TopLocationsTable data={dailyAreas} />
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer — reuse AlertDetails drawer, hide time range */}
                    <ReportFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        hideTimeRange={true}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={5}
                        rangeId={rangeId}
                        rangeName={rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteTarget ? (
                        <EventNoteDialog
                            event={noteTarget}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteTarget(null); }}
                            onSave={handleSaveNote}
                            saving={saving}
                        />
                    ) : null}

                    {/* Edit Reason Dialog */}
                    {reasonDialogOpen && reasonTarget ? (
                        <EditReasonDialog
                            event={reasonTarget}
                            reasonCodes={filterOptions.reasonCodes || []}
                            onClose={() => { setReasonDialogOpen(false); setReasonTarget(null); }}
                            onSave={handleSaveReason}
                            saving={saving}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default ExecutiveReportPage;
